import { BUSINESS } from "@/data/business";
import { BookOpen } from "@phosphor-icons/react";

const SECTIONS = [
  {
    h: "The free trial",
    p: "You get 10 days free. During the free trial you can only use the Childhood season. You do not need to enter any payment details to try the app.",
  },
  {
    h: "What happens after 10 days",
    p: "When your 10 free days end, your diary is paused for 30 days. During those 30 days you can still sign in to read your memories, download them as a PDF, upgrade to Lifetime Access, or delete everything yourself straight away. You can't add new memories while paused. We email you when your diary is paused and again 7 days before deletion. At the end of the 30 days, your account, memories, reflections and keepsakes are permanently deleted and cannot be recovered.",
  },
  {
    h: "No automatic charges",
    p: "We will never charge you automatically. We do not store your card or take money without you clicking \u201cPay\u201d. There is no subscription and no renewal.",
  },
  {
    h: "If you decide to join (Lifetime Access)",
    p: "You pay once. That's it \u2014 forever. You unlock all life seasons and can keep recording your memories for as long as you like.",
  },
  {
    h: "Optional extras (only if you ask)",
    p: "Things like a Yearly Summary, Life Story, or Ebook cost a small one-time fee each time you request one. We always show you the price first. You only pay if you tap \u201cPay\u201d. If you never request them, you never pay.",
  },
  {
    h: "Cancelling",
    p: "There's nothing to cancel \u2014 there's no subscription. If you don't join after your free trial, you simply stop. Your diary is paused for 30 days and then permanently deleted. You can delete your account yourself at any time from the \u201cYour data\u201d button in your diary.",
  },
  {
    h: "Your privacy",
    p: "Your memories are private. We never share or sell them. Only you can see your journey. To create an account we ask only for your first name, a username, a password and your email address (used only for account recovery and receipts). We never ask for your phone number or home address. Ebook photos never leave your device. Payment records (date, product, amount and payment reference) are kept after deletion because Australian tax law requires us to keep them for 5 years; they never contain your memories.",
  },
  {
    h: "How AI creates your Reflections and Ebook",
    p: "Optional Reflections and the Ebook are written by an AI service (Anthropic Claude Sonnet), called securely from our server \u2014 no AI key is ever in your browser. We send only the memories included in the product you choose, and the AI is instructed to use only your memories and never invent facts. You must tick a consent box before any memories are sent, and you should review AI-generated writing before downloading or sharing it. AI processing currently happens on cloud infrastructure outside Australia (United States and India). We cannot independently guarantee the AI provider's own data-retention or model-training practices; contact us if you need written confirmation. You can delete any generated Reflection or Ebook from your account at any time, and your original memories always remain private unless you choose to share them.",
  },
];

export default function Terms() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <div className="mb-8 flex items-center gap-2">
        <BookOpen size={26} weight="duotone" className="text-[#B08D57]" />
        <span className="font-serif text-xl text-[#2C2A28]">Your Life Journeys Matter</span>
      </div>
      <h1 className="font-serif text-4xl text-[#2C2A28]">Terms and Conditions</h1>
      <p className="mt-2 text-[#8A7F76]">
        Written in plain English. No hidden fees. No surprises.
      </p>

      <div className="mt-8 space-y-6">
        {SECTIONS.map((s) => (
          <div key={s.h} className="rounded-lg border border-[#4A4540]/10 bg-white p-5">
            <h2 className="font-serif text-xl text-[#2C2A28]">{s.h}</h2>
            <p className="mt-2 leading-relaxed text-[#4A4540]">{s.p}</p>
          </div>
        ))}
      </div>

      <div data-testid="terms-refunds" className="mt-6 rounded-lg border border-[#A84A32]/20 bg-[#FBF1EC] p-5">
        <h2 className="font-serif text-xl text-[#2C2A28]">Refunds</h2>
        <p className="mt-2 leading-relaxed text-[#4A4540]">
          Change of mind refunds are available within 14 days if the paid product hasn't been used, generated or downloaded. If an AI product fails or has a serious problem, we'll first create it again free; if that can't be fixed, you may receive a refund. Duplicate payments are always refunded. Your rights under the Australian Consumer Law are not affected.{" "}
          <a href="/refunds" data-testid="terms-refund-link" className="font-semibold text-[#A84A32] underline underline-offset-2">Read the full Refund Policy</a>.
        </p>
      </div>

      <div data-testid="terms-contact" className="mt-6 rounded-lg bg-[#2E3B31] p-5 text-[#FBF4E8]">
        <h2 className="font-serif text-xl">Who we are and how to contact us</h2>
        <p className="mt-2 text-sm leading-relaxed text-[#C9CFC4]">
          {BUSINESS.tradingAs} is operated by {BUSINESS.legalName}, trading as {BUSINESS.tradingAs}. Questions, privacy requests or refund requests:{" "}
          <a href={`mailto:${BUSINESS.email}`} className="font-semibold text-[#E8C27A] underline-offset-2 hover:underline">{BUSINESS.email}</a>. Please never send your password or full card details.
        </p>
      </div>

      <p className="mt-8 text-sm text-[#8A7F76]">
        © 2026 {BUSINESS.legalName}. {BUSINESS.tradingAs}™. All rights reserved.
      </p>
    </div>
  );
}
